
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar
} from '@/components/ui/sidebar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  Calendar,
  CreditCard,
  FileText,
  HelpCircle,
  BarChart3,
  Settings,
  UserCheck,
  UserPlus,
  Folder,
  Tag,
  MessageSquare,
  TrendingUp,
  DollarSign,
  ChevronDown,
  Award,
  Mail,
  Send,
  BarChart3 as EmailChart,
  FileText as EmailTemplate,
  Monitor,
  ClipboardList,
  Info,
  Sparkles
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

  const menuItems = [
  // Top-level items first
  {
    title: 'Dashboard',
    href: '/admin/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Admin Management',
    href: '/admin/admin-management',
    icon: Settings,
  },
  {
    title: 'Monitoring',
    href: '/admin/monitoring',
    icon: Monitor,
  },
  {
    title: 'Bookings',
    href: '/admin/bookings',
    icon: Calendar,
  },
  {
    title: 'Payments',
    href: '/admin/payments',
    icon: CreditCard,
  },
  {
    title: 'Financial Dashboard',
    href: '/admin/finances',
    icon: DollarSign,
  },
  {
    title: 'User Analytics',
    href: '/admin/user-analytics',
    icon: TrendingUp,
  },
  {
    title: 'Support',
    href: '/admin/support',
    icon: HelpCircle,
  },
  {
    title: 'Jobs Management',
    href: '/admin/jobs',
    icon: ClipboardList,
  },
  // Items with submenus
  {
    title: 'User Management',
    icon: Users,
    submenu: [
      {
        title: 'Practitioners',
        href: '/admin/users/practitioners',
        icon: UserCheck,
      },
      {
        title: 'Clients',
        href: '/admin/users/clients',
        icon: UserPlus,
      },
    ],
  },
  {
    title: 'Content Management',
    icon: FileText,
    submenu: [
      {
        title: 'Categories',
        href: '/admin/cms/categories',
        icon: Folder,
      },
      {
        title: 'Posts',
        href: '/admin/cms/posts',
        icon: FileText,
      },
      {
        title: 'Tags',
        href: '/admin/cms/tags',
        icon: Tag,
      },
      {
        title: 'Framework',
        href: '/admin/cms/therapy-modalities',
        icon: Sparkles,
      },
      {
        title: 'Notes Templates',
        href: '/admin/cms/notes-templates',
        icon: ClipboardList,
      },
      {
        title: 'Images',
        href: '/admin/cms/images',
        icon: FileText,
      },
      {
        title: 'Featured Practitioners',
        href: '/admin/cms/featured-practitioners',
        icon: Award,
      },
      {
        title: 'Partner Organizations',
        href: '/admin/cms/partner-organizations',
        icon: Users,
      },
    ],
  },
  {
    title: 'Email Management',
    icon: Mail,
    submenu: [
      {
        title: 'Overview',
        href: '/admin/emails',
        icon: Mail,
      },
      {
        title: 'Campaigns',
        href: '/admin/emails/campaigns',
        icon: Send,
      },
      {
        title: 'Templates',
        href: '/admin/emails/templates',
        icon: EmailTemplate,
      },
      {
        title: 'Email Logs',
        href: '/admin/emails/logs',
        icon: FileText,
      },
      {
        title: 'Analytics',
        href: '/admin/emails/analytics',
        icon: EmailChart,
      },
    ],
  },
  {
    title: 'Version Info',
    href: '/admin/version',
    icon: Info,
  },
];

const AdminSidebarContent = () => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev => 
      prev.includes(title) 
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  const isActive = (href: string) => location.pathname === href;
  const hasActiveChild = (submenu: any[]) => submenu.some(item => isActive(item.href));

  const renderMenuItem = (item: any) => {
    if (item.submenu) {
      const isExpanded = expandedItems.includes(item.title);
      const hasActive = hasActiveChild(item.submenu);
      
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton 
            tooltip={item.title}
            onClick={() => toggleExpanded(item.title)}
            isActive={hasActive}
            className="w-full justify-between"
          >
            <div className="flex items-center">
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.title}</span>
            </div>
            {(expanded || isMobile) && (
              <ChevronDown 
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  isExpanded ? "rotate-180" : ""
                )} 
              />
            )}
          </SidebarMenuButton>
          {isExpanded && (expanded || isMobile) && (
            <SidebarMenuSub>
              {item.submenu.map((subItem: any) => (
                <SidebarMenuSubItem key={subItem.href}>
                  <SidebarMenuSubButton 
                    onClick={() => navigate(subItem.href)}
                    isActive={isActive(subItem.href)}
                    className="cursor-pointer"
                  >
                    <subItem.icon className="mr-2 h-4 w-4" />
                    <span>{subItem.title}</span>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.href}>
        <SidebarMenuButton 
          tooltip={item.title}
          onClick={() => navigate(item.href)}
          isActive={isActive(item.href)}
          className="cursor-pointer"
        >
          <item.icon className="mr-2 h-4 w-4" />
          <span>{item.title}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <ScrollArea className="h-full">
      <SidebarMenu className="px-2">
        {menuItems.map(renderMenuItem)}
      </SidebarMenu>
    </ScrollArea>
  );
};

export default AdminSidebarContent;
